import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IDesign } from '../../../models/design.model';
import { ArtDirectorService } from '../../../services/art-director.service';
import { IDynamicIdentifier, IDynamicLottieChange, IStagePosition } from 'lottie-json-helper/lib/types';
import {
    BULLET_SCENES,
    CompositionTypeEnum,
    DynamicAsset,
    FRAMED_VIDEO_SCENES,
    GENERAL_SCENES,
    IComposition,
    IMAGE_SCENES,
    ProjectRecordingTypeEnum,
    TITLES_SCENES,
    VIDEO_AND_TEXT_SCENES,
} from 'src/app/models/defines';
import { DynamicLottie } from 'lottie-json-helper/lib/dynamic-lottie';
import { AspectRatioEnum } from '../../../models/project/edit/edit-model';
import { IVideoLayer } from '../../../models/project/take/layers/video-layer-model';

export interface IAddSceneDialogData {
    design: IDesign;
    callbackAsync: (chosenComposition: IComposition) => Promise<void>;
    currentComposition: IComposition | null;
    personalizedDynamics: IDynamicLottieChange[];
    aspectRatio: AspectRatioEnum;
    filter: {
        projectType: ProjectRecordingTypeEnum;
        videoLayers: IVideoLayer[];
    } | null;
}

export interface ICompositionAndStagePosition {
    composition: IComposition;
    stagePositions: IStagePosition[]; /// We don't use the extended one because we don't need the id, only to display the values (x,y,width,height)
}

export interface ISceneBank {
    name: string;
    compositionsAndStagePositions: ICompositionAndStagePosition[];
    type: CompositionTypeEnum[];
}

@Component({
    selector: 'app-scene-bank',
    templateUrl: './scene-bank.component.html',
    styleUrls: ['./scene-bank.component.scss'],
})
export class SceneBankComponent implements OnInit {
    personalizedDynamics: IDynamicLottieChange[];
    design: IDesign;
    chosenComposition: IComposition;
    isLoadingNewScene: boolean;
    localCompositions: ISceneBank[] = [];
    isCameraBank = false;
    videoLayers: IVideoLayer[];
    projectType: ProjectRecordingTypeEnum;
    aspectRatio: AspectRatioEnum;

    constructor(
        private artDirector: ArtDirectorService,
        public dialogRef: MatDialogRef<SceneBankComponent>,
        @Inject(MAT_DIALOG_DATA) public data: IAddSceneDialogData
    ) {
        this.videoLayers = data.filter?.videoLayers ?? [];
        this.projectType = data.filter?.projectType ?? ProjectRecordingTypeEnum.CAMERA;
        this.isCameraBank = this.projectType === ProjectRecordingTypeEnum.CAMERA;
        this.personalizedDynamics = data.personalizedDynamics;
        this.design = data?.design;
        this.aspectRatio = data.aspectRatio ?? AspectRatioEnum._16x9;
        this.chosenComposition = this.data.currentComposition;
    }

    ngOnInit(): void {
        this.initializeCompositionsAsync();
    }

    private async initializeCompositionsAsync() {
        if (this.design?.compositions) {
            this.localCompositions = this.initializeCompositions();
            const generalCompositions = this.localCompositions.find(
                (localComposition) => localComposition.type.length === 0
            );
            const promises: Promise<any>[] = [];
            for (const composition of this.design.compositions) {
                promises.push(this.filterAndLoadCompositionAsync(composition, generalCompositions));
            }
            await Promise.all(promises);
        }
    }

    private async filterAndLoadCompositionAsync(composition: IComposition, generalCompositions: ISceneBank) {
        const compositionAndStagePosition = await this.processCompositionAsync(composition);
        if (!compositionAndStagePosition) {
            return;
        }
        const videoLayers = this.videoLayers;
        const projectType = this.projectType;
        /// We limit the compositions so the user can't go under the stage positions of the length of the video layers!
        if (compositionAndStagePosition.stagePositions.length < videoLayers.length - 1) {
            return;
        }

        const targetComposition = this.localCompositions.find((scene) => {
            if (!scene) {
                return;
            }
            return scene.type.includes(compositionAndStagePosition.composition.type);
        });
        if (!targetComposition) {
            if (!generalCompositions) {
                return;
            }
            generalCompositions.compositionsAndStagePositions.push(compositionAndStagePosition);
            return;
        }
        if (!composition.name) {
            switch (composition.type) {
                case CompositionTypeEnum.IMG_SINGLE_STATIC:
                    composition.name = 'Image Only';
                    break;
                case CompositionTypeEnum.VID_CLEAN:
                    composition.name = 'Video Only';
                    break;
                case CompositionTypeEnum.THREE_IMG_SLIDES:
                    composition.name = '3 Image Carousel';
                    break;
                case CompositionTypeEnum.FOUR_IMG_FRAMES_TXT:
                    composition.name = '4 Image Slideshow';
                    break;
                case CompositionTypeEnum.BROWSER_MOCKUP:
                    composition.name = 'Image Inside Browser';
                    break;
                case CompositionTypeEnum.QUOTE_TXT:
                    composition.name = 'Quote';
                    break;
            }
        }
        targetComposition.compositionsAndStagePositions.push(compositionAndStagePosition);
    }

    chooseComposition(composition: IComposition): void {
        this.chosenComposition = composition;
    }

    async onConfirmClickedAsync() {
        if (this.chosenComposition._id === this.data.currentComposition?._id) {
            this.closeDialog();
            return;
        }
        this.isLoadingNewScene = true;

        await this.data.callbackAsync(this.chosenComposition);

        this.isLoadingNewScene = false;
        this.closeDialog();

        // this.projectApiService.addScene$(projectId, sceneId, params).subscribe({
        //     next: async (res) => {
        //         //// We have socket a socket event to deal with the scene
        //         this.closeDialog();
        //         this.isLoadingNewScene = false;
        //         return;
        //     },
        //     error: (error) => {
        //         console.log(error);
        //     },
        // });
    }

    closeDialog() {
        this.dialogRef.close();
    }

    private initializeCompositions() {
        const compositions: ISceneBank[] = [
            { name: 'Title Scenes', type: TITLES_SCENES, compositionsAndStagePositions: [] },
            { name: 'Image Scenes', type: IMAGE_SCENES, compositionsAndStagePositions: [] },
            { name: 'Video & Text Scenes', type: VIDEO_AND_TEXT_SCENES, compositionsAndStagePositions: [] },
            { name: 'Video Scenes', type: FRAMED_VIDEO_SCENES, compositionsAndStagePositions: [] },
            { name: 'List Scenes', type: BULLET_SCENES, compositionsAndStagePositions: [] },
            { name: 'General', type: GENERAL_SCENES, compositionsAndStagePositions: [] },
        ];
        return compositions;
    }

    // Function to process composition
    async processCompositionAsync(composition: IComposition) {
        const lottieAsset = await this.getLottieAssetAsync(composition);
        if (!lottieAsset || !lottieAsset.dynamics) {
            return;
        }

        const dynamicStagePositions = this.getDynamicStagePositions(lottieAsset);
        const stagePositions: IStagePosition[] = [];
        const compositionAndLayout: ICompositionAndStagePosition = {
            composition,
            stagePositions,
        };

        if (!dynamicStagePositions || dynamicStagePositions.length === 0) {
            // Returning the composition with an empty array of stage positions
            return compositionAndLayout;
        }

        // Finding all the stage positions for this lottie
        stagePositions.push(...this.getStagePositions(dynamicStagePositions, lottieAsset));

        return compositionAndLayout;
    }

    async getLottieAssetAsync(composition: IComposition) {
        const dynamicAsset = await this.artDirector.getBaseJsonAsync(composition.layouts[0]);

        return dynamicAsset;
    }

    getDynamicStagePositions(lottieAsset: DynamicAsset): IDynamicIdentifier[] {
        return lottieAsset.dynamics.filter((dynamic) => dynamic.type === 'stage_position');
    }

    getStagePositions(dynamicLotties: IDynamicLottieChange[], lottieAsset: DynamicAsset): IStagePosition[] {
        return dynamicLotties.map((dynamicLottie) =>
            DynamicLottie.getStagePositions(dynamicLottie, lottieAsset.height, lottieAsset.width)
        );
    }

    protected readonly AspectRatioEnum = AspectRatioEnum;
}
